import React, { useMemo, useState } from 'react';
import { Card, Badge, Avatar, Button } from '../../ui-v2';
import { ToggleGroup, ToggleItem } from '../../ui-v2.5';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { MODALIDAD, etiquetaDe } from '../compartidos/etiquetasEstado';
import { etiquetaConCita } from './progresoResumen';
import { FILTROS_PERSONAS, contarPersonas, filtrarPersonas } from './resumenPestanas';

/**
 * Acciones por fila (24/10), sólo con `puedeGestionar` (intervenciones.campanas.manage; la barrera
 * es el servidor):
 *   «Registrar intervención» — sin cita o con cita reservada (entonces, marcar ESA);
 *   «Deshacer» — la última intervención de la persona se registró por esa vía.
 *
 * Rediseño (Fase 1): el filtro se puede controlar desde fuera (`filtro` + `onFiltro`, para llevarlo en
 * la URL); sin ellos, como antes, lo guarda el componente. Los 7 filtros y sus recuentos, de
 * resumenPestanas.js.
 */
export default function SeccionUsuarios({
  usuarios, puedeGestionar = false, onRegistrar, onDeshacer, filtro: filtroFuera, onFiltro,
}) {
  const [filtroPropio, setFiltroPropio] = useState('todos');
  const filtro = filtroFuera || filtroPropio;
  const setFiltro = onFiltro || setFiltroPropio;

  const conteos = useMemo(() => contarPersonas(usuarios), [usuarios]);
  const filtrados = useMemo(() => filtrarPersonas(usuarios, filtro), [usuarios, filtro]);

  return (
    <Card className="overflow-hidden p-0 mb-6">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h2 className="text-base font-medium text-slate-900">Personas</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {filtrados.length} de {usuarios.length} mostradas
            </p>
          </div>
        </div>

        <ToggleGroup value={filtro} onChange={setFiltro} className="flex-wrap">
          {FILTROS_PERSONAS.map((f) => (
            <ToggleItem key={f.id} value={f.id} label={f.label} count={conteos[f.id]} />
          ))}
        </ToggleGroup>
      </div>

      {filtrados.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-slate-400">
          No hay personas en esta categoría.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filtrados.map((u) => (
            <FilaUsuario key={u.id} usuario={u} puedeGestionar={puedeGestionar} onRegistrar={onRegistrar} onDeshacer={onDeshacer} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function FilaUsuario({ usuario: u, puedeGestionar, onRegistrar, onDeshacer }) {
  const registrable = u.estado === 'pendiente_reserva' || u.estado === 'cita_reservada';
  return (
    <li className="px-5 py-3.5 hover:bg-slate-50/60 transition-colors">
      <div className="flex items-center gap-4">
        <Avatar name={u.nombre_completo} size="default" />

        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-900 truncate">{u.nombre_completo}</div>
          {u.email && (
            <div className="text-xs text-slate-400 truncate">{u.email}</div>
          )}
          {/* Lotes · de qué lote es y dónde trabaja (empresa y negocio del primer cargo). */}
          {(u.lote || u.empresa || u.negocio) && (
            <div className="text-[11px] text-slate-500 truncate">
              {[
                u.lote && `Lote ${u.lote.numero} · ${u.lote.nombre}`,
                u.empresa && u.empresa.nombre,
                u.negocio && u.negocio.nombre,
              ].filter(Boolean).join(' — ')}
            </div>
          )}
        </div>

        {/* Estado del usuario o subtareas */}
        <div className="hidden sm:flex flex-col items-end gap-1 shrink-0 min-w-[160px]">
          <EstadoUsuario usuario={u} />
          {u.estado === 'realizado' && u.subtareas_total > 0 && (
            <BarraSubtareas
              realizadas={u.subtareas_realizadas}
              total={u.subtareas_total}
            />
          )}
        </div>

        {/* Cita activa */}
        <div className="hidden md:block shrink-0 min-w-[140px] text-right">
          {u.cita ? (
            <>
              <div className="text-xs text-slate-700 tabular-nums">
                {/* Registrada sin cita (24/10): sin hora; se dice cómo se hizo. */}
                {formatFechaCorta(u.cita.fecha)} · {u.cita.hora_inicio || etiquetaDe(MODALIDAD, u.cita.modalidad).label}
              </div>
              <div className="text-[11px] text-slate-400 truncate">
                con {u.cita.tecnico?.nombre_completo || '—'}
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-300">—</div>
          )}
        </div>

        {/* Cancelaciones */}
        <div className="shrink-0 text-right hidden lg:block min-w-[80px]">
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">Cancel.</div>
          <div className="text-xs text-slate-700 tabular-nums">
            {u.cancelaciones_usadas} / {u.cancelaciones_max || '—'}
          </div>
        </div>

        {puedeGestionar && (
          <div className="shrink-0 flex flex-col items-end gap-1 min-w-[150px]">
            {registrable && (
              <Button size="sm" variant="outline" onClick={() => onRegistrar?.(u)}>Registrar intervención</Button>
            )}
            {u.intervencion_deshacible && (
              <Button size="sm" variant="ghost" onClick={() => onDeshacer?.(u)}>Deshacer</Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function EstadoUsuario({ usuario: u }) {
  if (u.estado === 'pendiente_reserva') {
    return <Badge variant="warning">Sin cita</Badge>;
  }
  if (u.estado === 'cita_reservada') {
    // 3/11 · B4 — con progreso guardado: «Con cita · 4/11».
    return <Badge variant="success">{etiquetaConCita(u)}</Badge>;
  }
  if (u.estado === 'realizado') {
    if (u.tiene_subtareas_pendientes) {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Parcial
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> Completado
      </span>
    );
  }
  if (u.estado === 'cancelado_usuario' || u.estado === 'cancelado_sistemas') {
    return <Badge variant="destructive">Cancelado</Badge>;
  }
  return <Badge variant="secondary">{u.estado}</Badge>;
}

function BarraSubtareas({ realizadas, total }) {
  const pct = total > 0 ? (realizadas / total) * 100 : 0;
  const completo = realizadas === total;
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-20 h-1 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={'h-full transition-all ' + (completo ? 'bg-emerald-500' : 'bg-amber-400')}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap">
        {realizadas}/{total}
      </span>
    </div>
  );
}

function formatFechaCorta(s) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short',
  });
}
